import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  AccountTypes,
  CidAccountTypes,
  DualAccountTypes,
  PacAccountTypes,
  PacAccountTypesValue,
  StarsAccountTypesValue
} from '@core/constants';
import { DmeposAccountTypes } from '@core/constants/dmepos-account-type';
import { DmeposAccountTypesValue } from '@core/constants/dmepos-account-types-value';
import { PAC_ACCOUNT_TYPE_ENUM } from '@core/enums/pac-account-type.enum';
import { STARS_ACCOUNT_TYPE_ENUM } from '@core/enums/stars-account-type.enum';
import { TenantsService } from '@core/services';
import { MessageService, SelectItem } from 'primeng/api';
import { RadioButtonClickEvent } from 'primeng/radiobutton';
import { TenantNames } from '@mynexus/mynexus-ui-lib';
import { map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-account-switch',
    templateUrl: './account-switch.component.html',
    styleUrls: ['./account-switch.component.scss'],
    standalone: false
})
export class AccountSwitchComponent implements OnInit {
  private accountValue!: string;
  private accountTypesValue!: SelectItem[];
  private readonly healthPlanCaseManagerAccountType = 'Health Plan Case Manager';
  private readonly chappProviderAccountType = AccountTypes.CHAPPProvider;
  tenantList: SelectItem[] = [];
  private tenantNameToIdMap: Map<string, string> = new Map();

  @Input() userId = '';
  @Input() hasSelectedProviders = false;
  @Input() disableRadioButtons: boolean = false;
  @Input() removeCHAPPAccountType = false;
  @Input() healthPlan = '';
  @Input() set tenantId(value: string) {
    this.tenant = value;

    // Get tenant name from ID for comparison
    const tenantName =  this.getTenantNameById(value);

    switch (tenantName) {
      case TenantNames.Anthem:
      case '':
        this.availableAccountTypes = this.accountTypesValue;
        break;
      case TenantNames.Regence:
        this.availableAccountTypes = this.accountTypesValue.filter(
          (e: SelectItem) =>
            [...PacAccountTypesValue, 'Home Health Agency', 'Physician', 'Health Plan Case Manager',
              'Hospital Case Manager/Discharge Planner'].includes(e.label as PAC_ACCOUNT_TYPE_ENUM | string)
        )
        break;
      case TenantNames.Wellpoint:
        this.availableAccountTypes = this.accountTypesValue.filter((e: SelectItem) =>
          [...PacAccountTypesValue,
            'Physician',
            'Health Plan Case Manager',
          ].includes(e.label as PAC_ACCOUNT_TYPE_ENUM | string)
        )
        break;
      case TenantNames.BCBSRI:
      case TenantNames.NextBlue:
        this.availableAccountTypes = this.accountTypesValue.filter((e: SelectItem) =>
          [...PacAccountTypesValue,
            'Physician',
            'Health Plan Case Manager',
          ].includes(e.label as PAC_ACCOUNT_TYPE_ENUM | string)
        )
        break;
      default:
        this.availableAccountTypes = this.accountTypesValue.filter(
          (e: SelectItem) =>
            ![...PacAccountTypesValue, ...StarsAccountTypesValue, ...DmeposAccountTypesValue].includes(e.label as STARS_ACCOUNT_TYPE_ENUM)
        )
        break;
    }

    if (this.tenant && tenantName === TenantNames.Regence) {
      this.availableAccountTypes = this.availableAccountTypes.filter(x => x.label != "Home Health Billing Office" && x.label != "Home Health Admin");
    }      

    const centralizedDepartmentToggleForHP = !!this.availableAccountTypes.find(accountType => CidAccountTypes.find(cidAccountType => cidAccountType === accountType.value));
    this.showCentralizedDepartmentToggleForHP.emit(centralizedDepartmentToggleForHP);
    this.removeCHAPPProviderOption();
  }

  @Input() set account(value: string) {
    this.accountValue = value;
    this.buildAccountSwitchForm();
  }

  get account(): string {
    return this.accountValue;
  }

  @Input() set accountTypes(value: SelectItem[]) {
    this.accountTypesValue = value;
  }

  get accountTypes(): SelectItem[] {
    return this.accountTypesValue;
  }

  @Output() accountTypeNew = new EventEmitter<string>();
  @Output() showCentralizedDepartmentToggleForHP = new EventEmitter<boolean>();

  tenant!: string;
  showDialog = false;
  type!: SelectItem;
  accountSwitchForm!: FormGroup;
  newAccount!: string;
  availableAccountTypes!: SelectItem[];
  private destroyRef = inject(DestroyRef);

  constructor(private messageService: MessageService, private tenantsService: TenantsService) { }

  ngOnInit(): void {
    this.getTenants();
  }

  buildAccountSwitchForm(): void {
    this.accountSwitchForm = new FormGroup({
      accountTypeControl: new FormControl(this.account)
    });
  }

  getTenants(): void {
    this.tenantsService
      .getTenants()
      .pipe(
        map((list) => {
          // Create a map for tenant name to ID lookup
          list.forEach(tenant => {
            this.tenantNameToIdMap.set(tenant.name, tenant.tenantId);
          });
          return list.map((el) => ({ value: el.tenantId, label: el.name }));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.tenantList = res;
      });
  }

  private getTenantNameById(tenantId: string): string {
    for (const [name, id] of this.tenantNameToIdMap.entries()) {
      if (id === tenantId) {
        return name;
      }
    }
    return '';
  }

  removeCHAPPProviderOption() {
    this.availableAccountTypes = this.availableAccountTypes.filter(account => account.value != this.chappProviderAccountType);
  }

  accountTypeChange(event: RadioButtonClickEvent){
    const accountType = event?.value;
    const isChangedToFromDualAccountTypes =
      (!DualAccountTypes.includes(this.accountValue) && DualAccountTypes.includes(accountType)
        || DualAccountTypes.includes(this.accountValue) && !DualAccountTypes.includes(accountType));
    const isChangedToFromPacAccountTypes =
      (!PacAccountTypes.includes(this.accountValue) && PacAccountTypes.includes(accountType))
      || (PacAccountTypes.includes(this.accountValue) && !PacAccountTypes.includes(accountType));
    const isChangedToFromDmeposTypes =
      (!DmeposAccountTypes.includes(this.accountValue) && DmeposAccountTypes.includes(accountType))
      || (DmeposAccountTypes.includes(this.accountValue) && !DmeposAccountTypes.includes(accountType));

    if (this.hasSelectedProviders && this.healthPlan === ''){
      this.displayWarningMessage("Please choose a Health Plan");
    } else if (this.hasSelectedProviders && (isChangedToFromDualAccountTypes || isChangedToFromPacAccountTypes || isChangedToFromDmeposTypes)) {
      this.displayWarningMessage("Please remove the selected providers before changing the Account Type");
    } else if (this.getTenantNameById(this.tenant) === TenantNames.Regence && accountType === this.healthPlanCaseManagerAccountType) {
      this.displayWarningMessageForRegenceHPCM();
    } else {
      this.showDialogWindow(accountType);
    }
  }

  showDialogWindow(val: string): void {
    if (this.account === val) {
      return;
    }
    if (this.tenant && val) {
      this.showDialog = true;
      this.newAccount = val;
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        key: 'warn',
        detail: 'Please choose a Health Plan',
        life: 5000
      });
      this.accountSwitchForm
        .get('accountTypeControl')
        ?.setValue(this.account, { emitEvent: false });
    }
  }

  proceedUpdate(): void {
    this.showDialog = false;

    const newAccountType = {
      accountTypeName: this.newAccount,
      tenantId: this.tenant
    };
    this.accountSwitchForm
      .get('accountTypeControl')
      ?.setValue(this.newAccount, { emitEvent: false });
    this.tenantsService.updateAccountType(this.userId, newAccountType).subscribe(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        key: 'switched',
        detail: 'Account type changed',
        life: 5000
      });
      this.accountTypeNew.emit(this.newAccount);
    });
  }

  cancelUpdate(): void {
    this.showDialog = false;
    this.accountSwitchForm
      .get('accountTypeControl')
      ?.setValue(this.account, { emitEvent: false });
  }

  private displayWarningMessageForRegenceHPCM(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Cannot Change Health Plan and Account Type',
      key: 'warn',
      detail: 'Regence Health Plan Case Managers must register using the Single Sign-On (SSO) process.',
      life: 5000
    });
    this.accountSwitchForm.get('accountTypeControl')?.setValue(this.account, { emitEvent: false });
  }

  private displayWarningMessage(message: string): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Warning',
      key: 'warn',
      detail: message,
      life: 5000
    });
    this.accountSwitchForm
      .get('accountTypeControl')
      ?.setValue(this.account, { emitEvent: false });
  }
}